﻿/*
 * Copyright (c) 2024-2026. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */

import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react'
import {useAppDispatch} from 'helpers/store'
import {useTranslation} from 'react-i18next'
import clsx from 'clsx'

import Dates from 'utils/Dates'

import {useTypedSelector} from 'helpers/reducers'

import {ReservationsTableRequest} from 'types/requests/ReservationsTableRequest'
import {type OnlineReservationsState, setTableState} from 'store/reservations/OnlineReservationsReducer'
import UiPage from 'components/Ui/UiPage'
import UiPageHeaderToolbar from 'components/Ui/UiPageHeaderToolbar'
import BookingSellerReservationsTable from 'pages/Front/Booking/Seller/BookingSellerReservationsTable'
import UiExportButton from 'components/Ui/UiExportButtons'

import {TableInitBase, TableUiState, useTable} from 'utils/hooks/useTable'
import UiTableHeaderResetButton from 'components/Ui/UiTableHeaderResetButton'
import UiTableHeaderFiltersToggleButton from 'components/Ui/UiTableHeaderFiltersToggleButton'
import {useReservationsQuery} from 'utils/hooks/useReservationsQuery'
import {buildTableRequest} from 'utils/table/query'
import {mergeFilterValues} from 'utils/table/filters'
import {ReservationStatus} from 'types/enums/ReservationStatus'
import {applyTablePage} from 'utils/table/state'
import {BookingSellerReservationsProvider} from 'pages/Front/Booking/Seller/BookingSellerReservationsContext'
import {useBookingSellerReservationsColumns} from 'pages/Front/Booking/Seller/components/useBookingSellerReservationsColumns'
import UiTableSaveViewButton from 'components/Ui/UiTableSaveViewButton'
import {TableViewId} from 'types/enums/specific/TableViewId'
import ReservationsAction from 'store/reservations/ReservationsAction'
import {downloadHandler} from 'utils/sellerUtils'
import {showExportError} from 'utils/exportUtils'
import {
    mapReservationColumnId,
    mapReservationColumnIdForUi,
    DEFAULT_RESERVATIONS_TABLE_SORTING,
    type ReservationFilterEntry,
    transformReservationSearch,
} from 'utils/reservations/reservationTableUtils'
import { buildTableExportPayloadFromTable } from 'utils/table/tableColumnExport'

const OnlineReservations: React.FC = () => {
    const dispatch = useAppDispatch()
    const { t } = useTranslation()
    const sellerId = useTypedSelector((state) => state.sessionState.session?.uid) || null
    const reservationsState: OnlineReservationsState = useTypedSelector((state) => state.onlineReservationsState)
    const tableState = reservationsState.tableState
    const reservations = reservationsState.reservations || []

    const [isTableStateSame, setIsTableStateSame] = useState<boolean>(false)
    const lastParamsRef = useRef<string>('')
    const tableStateSearch = useMemo<ReservationFilterEntry[]>(
        () =>
            Array.isArray(tableState?.search)
                ? (tableState.search as ReservationFilterEntry[])
                : [],
        [tableState]
    )
    const tableStateSorting = useMemo(
        () => (Array.isArray(tableState?.sorting) ? tableState.sorting : []),
        [tableState]
    )
    const tableDefaultData = reservations
    const onlineReservationsSearch = useMemo<ReservationFilterEntry[]>(
        () => [{ id: 'status', value: [ReservationStatus.Reserved] }],
        []
    )

    const originReservationFilterStatus = ReservationStatus.All

    const defaultReservationsTableUi: TableUiState = {
        pageIndex: 0,
        pageSize: 50,
        globalFilter: '',
        columnFilters: [],
        sorting: DEFAULT_RESERVATIONS_TABLE_SORTING,
    }

    const normalizedTableStateSearch = useMemo<ReservationFilterEntry[]>(() => {
        const normalized: ReservationFilterEntry[] = []

        for (const filterItem of tableStateSearch) {
            if (!filterItem?.id) continue

            const normalizedId = mapReservationColumnIdForUi(filterItem.id)
            const normalizedValue =
                normalizedId === 'seller_id' &&
                !Array.isArray(filterItem.value) &&
                filterItem.value != null
                    ? [filterItem.value]
                    : filterItem.value

            const existingIndex = normalized.findIndex(
                (entry) => entry.id === normalizedId
            )

            if (existingIndex === -1) {
                normalized.push({
                    id: normalizedId,
                    value: normalizedValue,
                })
                continue
            }

            normalized[existingIndex] = {
                id: normalizedId,
                value: mergeFilterValues(
                    normalized[existingIndex].value,
                    normalizedValue
                ),
            }
        }

        return normalized
    }, [tableStateSearch])

    const normalizedTableStateSorting = useMemo(
        () =>
            tableStateSorting.map((sortItem) => ({
                ...sortItem,
                id: mapReservationColumnIdForUi(sortItem.id),
            })),
        [tableStateSorting]
    )

    const columns = useBookingSellerReservationsColumns(() => {}, {
        readOnly: true,
		isOnlineOnly: true,
    })


    const tableInit = useTable({
        tableId: TableViewId.OnlineReservations,
        defaultData: tableDefaultData,
        columns: columns,
        enableRowSelection: true,
        defaultRecordsPage: tableState?.page && tableState.page !== 0 ? tableState.page - 1 : 0,
        defaultRecordsPerPage: tableState?.per_page || 50,
        defaultColumnFilters:
            normalizedTableStateSearch.length > 0
                ? [...normalizedTableStateSearch]
                : [],
        defaultGlobalFilter:
            (normalizedTableStateSearch.find((s) => s.id === 'all')
                ?.value as string) || '',
        defaultSorting:
            normalizedTableStateSorting.length > 0
                ? [...normalizedTableStateSorting]
                : [...DEFAULT_RESERVATIONS_TABLE_SORTING],
        defaultColumnVisibility: tableState?.columnVisibility ?? defaultReservationsTableUi.columnVisibility,
    })

    const {
        pageIndex,
        pageSize,
        globalFilter,
        columnFilters,
        sorting,
        columnVisibility,
        clearNullableFilters,
        setIsLoading,
        setLastColumnFilters,
    } = tableInit

    const tableQueryState = useMemo(
        () =>
            ({
                pageIndex,
                pageSize,
                globalFilter,
                columnFilters,
                sorting,
                columnVisibility,
                clearNullableFilters,
            }) as unknown as TableInitBase,
        [
            pageIndex,
            pageSize,
            globalFilter,
            columnFilters,
            sorting,
            columnVisibility,
            clearNullableFilters,
        ]
    )

    const transformedSorting = useMemo(
        () => sorting.map((sortItem) => ({ ...sortItem, id: mapReservationColumnId(sortItem.id) })),
        [sorting]
    )

    const reservationsParams: ReservationsTableRequest = useMemo(() => {
        const params = buildTableRequest({
            tableInit: tableQueryState,
            includeGlobalAsAll: true,
            encodeSearchAsJsonString: false,
        })

        return {
            page: params.page,
            per_page: params.per_page,
            search: onlineReservationsSearch,
            filter: {},
            sorting: transformedSorting,
            columnVisibility,
        }
    }, [tableQueryState, transformedSorting, columnVisibility, onlineReservationsSearch])

    const reservationsTableStateParams: ReservationsTableRequest = useMemo(() => {
        const params = buildTableRequest({
            tableInit: tableQueryState,
            includeGlobalAsAll: true,
            encodeSearchAsJsonString: false,
        })

        return {
            page: params.page,
            per_page: params.per_page,
            search: onlineReservationsSearch,
            filter: {},
            sorting,
            columnVisibility,
        }
    }, [tableQueryState, sorting, columnVisibility, onlineReservationsSearch])

    const { data: reservationsData, isLoading: isQueryLoading } = useReservationsQuery(
        sellerId,
        reservationsParams,
        true,
        {
            endpoint: '/admin/reservations/table/future',
            includeSellerId: false,
            includeSearch: true,
            syncToRedux: true,
			isOnlineReservations: true,
            tableStateParams: reservationsTableStateParams,
        }
    )



    useEffect(() => {
        if (reservationsData) {
            applyTablePage(tableInit, reservationsData)
        }
    }, [reservationsData, tableInit])

    useEffect(() => {
        setIsLoading(isQueryLoading)
    }, [isQueryLoading, setIsLoading])

    const onChangeFilterStatusHandler = (id: ReservationStatus) => {
        const itemIndex = tableInit.columnFilters.findIndex((item) => item.id === 'status')
        if (itemIndex !== -1) {
            if (id !== originReservationFilterStatus) {
                const newFilter = [...tableInit.columnFilters]
                newFilter[itemIndex] = { ...newFilter[itemIndex], value: id }
                tableInit.setColumnFilters(newFilter)
            } else {
                tableInit.setColumnFilters((prev) => prev.filter((f) => f.id !== 'status'))
            }
        } else if (id !== originReservationFilterStatus) {
            tableInit.setColumnFilters([...tableInit.columnFilters, { id: 'status', value: id }])
        }
    }

    const selectedTabId = useMemo(() => {
        const statusValue = tableInit.getSelectedValueFilter('status')
        if (Array.isArray(statusValue)) {
            return (statusValue[0] as ReservationStatus) || ReservationStatus.All
        }
        return (statusValue as ReservationStatus) ?? ReservationStatus.All
    }, [tableInit])

    useEffect(() => {
        const currentParams = JSON.stringify(reservationsParams)

        const isSame = lastParamsRef.current === currentParams
        setIsTableStateSame(isSame)
        lastParamsRef.current = currentParams

        setLastColumnFilters(columnFilters)

        if (!isSame) {
            dispatch(setTableState(reservationsTableStateParams))
        }
    }, [
        reservationsParams,
        reservationsTableStateParams,
        columnFilters,
        setLastColumnFilters,
        dispatch,
    ])

    const handleExportXlsx = useCallback(async () => {
        const exportParams = {
            ...buildTableRequest({
                tableInit: tableQueryState,
                extras: {
                    view: 'future',
                    filter: reservationsParams.filter,
                    export: 'csv',
                },
                includeGlobalAsAll: true,
                includeColumnVisibility: false,
                splitSorting: true,
                encodeSearchAsJsonString: false,
                transformSearch: transformReservationSearch,
                transformSorting: (sortItems) =>
                    sortItems.map((sortItem) => ({
                        ...sortItem,
                        id: mapReservationColumnId(sortItem.id),
                    })),
            }),
            ...buildTableExportPayloadFromTable(tableInit.table),
        }

        try {
            const url = await dispatch(ReservationsAction.fetchExportReservationsXlsx(exportParams))
            await downloadHandler(url, `Reservations_${Dates.ymd(new Date())}.csv`)
        } catch (error) {
            showExportError(error, t('Export failed'), 'Online reservations')
        }
    }, [dispatch, reservationsParams.filter, t, tableQueryState, tableInit.table])

    return (
        <UiPage
            componentName={'OnlineReservations'}
            header={
                <UiPageHeaderToolbar
                    // tabs={filterStatus}
                    // tabsVariant={UiTabVariant.Group}
                    selectedTabId={selectedTabId}
                    onTabChange={(tab) => onChangeFilterStatusHandler(tab as ReservationStatus)}
                    exportActions={
                        <div className={clsx('flex items-center gap-3')}>
                            <UiExportButton
                                tableInit={tableInit}
                                filename={`Reservations_${Dates.ymd(new Date())}`}
                                onXlsxClick={handleExportXlsx}
                            />
                            <UiTableSaveViewButton
                                tableInit={tableInit}
                                tableId={TableViewId.OnlineReservations}
                            />
                            <UiTableHeaderFiltersToggleButton tableInit={tableInit} />
                            <UiTableHeaderResetButton
                                tableInit={tableInit}
                                tableId={TableViewId.OnlineReservations}
                                defaultUiState={defaultReservationsTableUi}
                                title={t('Reset') as string}
                                onAfterReset={() => {
                                    dispatch(
                                        setTableState({
                                            ...reservationsTableStateParams,
                                            ...defaultReservationsTableUi,
                                            search: onlineReservationsSearch,
                                            filter: {},
                                            per_page: defaultReservationsTableUi.pageSize,
                                            page: defaultReservationsTableUi.pageIndex + 1,
                                        })
                                    )
                                }}
                            />
                        </div>
                    }
                />
            }
        >
            <div className={clsx('OnlineReservationsTableWrapper', 'flex-1 min-h-0')}>
                <BookingSellerReservationsProvider
                    value={{
                        tableInit,
                        isTableStateSame,
                        onlineOnly: true,
                        onRowClick: undefined,
                    }}
                >
                    <BookingSellerReservationsTable isOnlineOnly={true} />
                </BookingSellerReservationsProvider>
            </div>
        </UiPage>
    )
}

export default OnlineReservations
